import React, { useRef } from 'react'
import cx from 'classnames'

import reducer, { EActions, initialState, TInitialState } from './reducer'
import { isRedirectToPdpCandidate } from './utils'
import ClientApi from '../../../../api/clientApi'
import { buildUrlFromSuggestion, EPathSegment, useOutsideClick } from '../../../../utils'
import { trackSearchResultEvent, trackSearchSentEvent } from '../../../../utils/gtm'
import AppContext, { TAppContext } from '../../../../components/AppContext/AppContext'

import { TSuggestElement } from '../../../../types/factFinder'

import Icon, { EIconId } from '../../../../components/icons'
import { FocusOverlay } from '../../../../components/FocusOverlay/FocusOverlay'
import IconClose from '../../../../components/icons/IconClose'

const LOWER_LIMIT_BEFORE_SEARCH = 2

const SearchBar: React.FC = () => {
  const [state, dispatch] = React.useReducer(reducer, initialState)
  const searchBarRef = React.useRef<HTMLDivElement>(null)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const suggestionRefs: HTMLLIElement[] = []
  const { locale } = React.useContext<TAppContext>(AppContext)
  const [searchTerm, setSearchTerm] = React.useState('')

  React.useEffect(() => {
    if (typeof location !== 'undefined' && location.pathname.match(/^\/dpl\/[cpm]\/search$/)) {
      const match = location.search.match(/[&?]q=([^&]*)/)
      if (Array.isArray(match) && match[1]) {
        setSearchTerm(decodeURIComponent(match[1].replace(/\+/g, ' ')))
      }
    }
  }, [typeof location !== 'undefined' && location.pathname])

  React.useEffect(() => {
    updateSuggestions(state.searchTerm).then(() => null)
  }, [state.searchTerm])

  useOutsideClick(searchBarRef, () => dispatch({ type: EActions.CLEAR_SUGGESTIONS }), state.suggestions.length > 0)

  const handleOnChangeSearchTerm = (searchValue) => dispatch({ type: EActions.SET_SEARCH_TERM, payload: searchValue })

  const updateSuggestions = async (value: string) => {
    if (value.length > LOWER_LIMIT_BEFORE_SEARCH) {
      let result
      dispatch({ type: EActions.LOAD_SUGGESTIONS_START })
      try {
        const response = await ClientApi.getSuggestions(value, locale)
        result = response.data
      } catch (e) {
        result = []
      }
      dispatch({ type: EActions.LOAD_SUGGESTIONS_END, payload: result })
    } else {
      if (state.suggestions.length > 0) {
        dispatch({ type: EActions.CLEAR_SUGGESTIONS })
      }
    }
  }

  const handleKeyDownEvent = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { suggestions, selectedSuggestion }: TInitialState = state
    const suggestionsCount = suggestions.length
    if (suggestionsCount > 0) {
      switch (e.key) {
        case 'ArrowUp': {
          const payload: number = selectedSuggestion === -1 ? suggestionsCount - 1 : selectedSuggestion - 1
          dispatch({ type: EActions.SELECT_SUGGESTION, payload })
          break
        }
        case 'ArrowDown': {
          const payload: number = selectedSuggestion === suggestionsCount - 1 ? -1 : selectedSuggestion + 1
          dispatch({ type: EActions.SELECT_SUGGESTION, payload })
          break
        }
        case 'Enter': {
          if (selectedSuggestion > -1) {
            e.preventDefault()
            handleSelectSuggestion(selectedSuggestion)
          }
          break
        }
      }
    }
  }

  const handleSelectSuggestion = (index: number) => {
    const ref = suggestionRefs[index]
    ref && ref.click()
  }

  const handleOnClickSuggestion = (suggestion: TSuggestElement) => {
    if (isRedirectToPdpCandidate(state.searchTerm, state.suggestions)) {
      trackSearchResultEvent({
        searchTermTyped: state.searchTerm,
        searchTermSent: suggestion.name,
      })
    }
    trackSearchSentEvent({
      searchTerm: state.searchTerm,
      suggestion,
      selectedSuggestion: state.suggestions.indexOf(suggestion),
      totalSuggestions: state.suggestions.length,
    })
  }

  const handleOnSubmitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    if (state.isLoading) {
      e.nativeEvent.preventDefault()
      return
    }
    if (!state.searchTerm) {
      e.nativeEvent.preventDefault()
      searchInputRef.current?.focus()
      return
    }
    if (isRedirectToPdpCandidate(state.searchTerm, state.suggestions)) {
      // check :: searchTermSent === typed in search term
      trackSearchResultEvent({ searchTermTyped: state.searchTerm })
      return
    }
    trackSearchSentEvent({
      searchTerm: state.searchTerm,
      totalSuggestions: state.suggestions.length,
    })
  }

  const createSplitter = (searchTerm: string) => {
    try {
      return new RegExp(`(${state.searchTerm})`, 'gi')
    } catch (e) {
      return searchTerm
    }
  }

  function clearSearchTerm() {
    searchInputRef.current?.focus()
    setSearchTerm('')
  }

  const formRef = useRef<HTMLFormElement>(null)
  return (
    <div className="relative mb-12 sm:mt-4 lg:mt-12" ref={searchBarRef}>
      <FocusOverlay
        heightPx={40}
        offsetPx={12}
        visible={state.suggestions.length > 0}
        elementRef={formRef}
        onClick={() => dispatch({ type: EActions.CLEAR_SUGGESTIONS })}
      />
      <form
        ref={formRef}
        action={EPathSegment.SEARCH}
        onSubmit={handleOnSubmitSearch}
        className="flex items-center py-0 bg-lightgrey-light absolute left-0 right-0 h-40"
        style={{ transform: 'translateY(-100%)', zIndex: 19 }}
      >
        <input
          value={searchTerm}
          ref={searchInputRef}
          type="search"
          placeholder="Suchbegriff, Bestellnummer"
          name="q"
          onChange={(e) => {
            setSearchTerm(e.target.value)
            handleOnChangeSearchTerm(e.target.value)
          }}
          onKeyDown={handleKeyDownEvent}
          autoComplete="off"
          className={cx([
            'flex-grow text-16 bg-transparent outline-none px-16 search-input',
            'placeholder-iron placeholder-focus-hide color-dark h-full',
          ])}
          style={{
            minWidth: '20px',
          }}
        />
        {searchTerm && (
          <button type="button" onClick={clearSearchTerm} className="mr-12">
            <IconClose />
          </button>
        )}
        <span
          className={cx('h-24 w-2 bg-lightgrey', {
            invisible: searchTerm.length > 0,
          })}
        />

        <button
          type="submit"
          className={cx('flex items-center px-12 sm:px-48 lg:px-60 h-full', {
            'text-white': searchTerm.length > 0,
          })}
          style={searchTerm.length > 0 ? { backgroundColor: '#ff3c28' /* bg-brand-orange */ } : undefined}
        >
          <Icon id={EIconId.MAGNIFIER} className="w-24" />
          <span className="font-bold ml-4 sm:ml-12">Suchen</span>
        </button>
      </form>

      {state.suggestions.length > 0 && (
        <ul
          className="absolute left-0 bg-white text-14 uif-shadow"
          style={{ left: '-12px', right: '-12px', zIndex: 110 }}
        >
          {state.suggestions.map((suggestion: TSuggestElement, index: number) => {
            // use searchTerm string as splitter when searchTerm is not valid regex
            const splitter = createSplitter(state.searchTerm)
            const chunks = suggestion.name.split(splitter)
            return (
              <li
                key={suggestion.name}
                ref={(ref) => ref && (suggestionRefs[index] = ref)}
                onClick={() => handleOnClickSuggestion(suggestion)}
                className={cx('hover:bg-lightgrey-light transition-color duration-150', {
                  'bg-lightgrey-light': index === state.selectedSuggestion,
                })}
              >
                <a href={`${buildUrlFromSuggestion(suggestion)}`} className="block px-16 py-8">
                  {chunks.map((chunk: string, index: number) => (
                    <span
                      key={index}
                      className={cx({
                        'font-bold': chunk.toLowerCase() === state.searchTerm.toLowerCase(),
                      })}
                    >
                      {chunk}
                    </span>
                  ))}
                </a>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default SearchBar
